import React, { useState } from 'react';
import { REPORT_REASONS, reportContent, friendlyCallError, compactCount } from '../../utils/reviews';
import { useConfirm } from '../ConfirmDialog';

// Small pieces shared by the composer, post cards and replies.

// 1–5 stars, optional. Clicking the current value again clears it, which is
// how you turn a review back into a comment before posting.
export function StarInput({ value, onChange, disabled }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value || 0;
  return (
    <div
      className="star-input"
      role="radiogroup"
      aria-label="Rating (optional)"
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          className={`star-input__star${n <= shown ? ' star-input__star--on' : ''}`}
          disabled={disabled}
          onPointerEnter={e => { if (e.pointerType === 'mouse') setHover(n); }}
          onClick={() => { setHover(0); onChange(value === n ? null : n); }}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export function Stars({ value }) {
  const n = Number(value) || 0;
  return (
    <span className="post-stars" aria-label={`${n} out of 5 stars`}>
      {'★★★★★'.slice(0, n)}
      <span className="post-stars__off">{'★★★★★'.slice(n)}</span>
    </span>
  );
}

// Initial in a coloured circle. The colour is derived from the handle so the
// same person looks the same everywhere without storing an avatar.
const AVATAR_COLORS = ['#8B1A1A', '#B4491F', '#A0671A', '#4D7A2B', '#1F6F6B', '#2B5C8A', '#5B3F8C', '#8A2F6B'];
export function Avatar({ name, restaurant }) {
  const label = String(name || '?');
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return (
    <span
      className={`post-avatar${restaurant ? ' post-avatar--restaurant' : ''}`}
      style={restaurant ? undefined : { background: AVATAR_COLORS[h % AVATAR_COLORS.length] }}
      aria-hidden="true"
    >
      {label.replace(/[^A-Za-z0-9]/g, '').charAt(0).toUpperCase() || '?'}
    </span>
  );
}

export function AuthorBadges({ item }) {
  return (
    <>
      {item.isRestaurantResponse && (
        <span className="post-badge post-badge--restaurant" title="The venue itself">
          Owner
        </span>
      )}
    </>
  );
}

export function LikeButton({ liked, count, onClick, busy }) {
  return (
    <button
      type="button"
      className={`post-action post-like${liked ? ' post-like--on' : ''}`}
      onClick={onClick}
      disabled={busy}
      aria-pressed={liked}
      aria-label={liked ? 'Unlike' : 'Like'}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 7.9 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.4 4.3 6.8-1.8 4.6-9.3 9.2-9.3 9.2z"
          fill={liked ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
      <span>{compactCount(count)}</span>
    </button>
  );
}

// DEMO TOOLS (admin only, pre-launch): set a like count by hand. `onSet(n)`
// saves it; purgeDemoContent later puts the real count back.
export function DemoLikesButton({ count, onSet }) {
  const [error, setError] = useState('');

  async function click() {
    const answer = window.prompt('Set likes to (demo):', String(count || 0));
    if (answer == null) return;
    const n = Number(answer.trim());
    if (!Number.isInteger(n) || n < 0) { setError('Enter a whole number.'); return; }
    setError('');
    try {
      await onSet(n);
    } catch (err) {
      setError(friendlyCallError(err));
    }
  }

  return (
    <>
      <button type="button" className="post-action post-action--quiet" onClick={click}>Set likes</button>
      {error && <span className="post-report__error" role="alert">{error}</span>}
    </>
  );
}

// Shown only on your own post or reply. Asks first: there's no undo.
export function DeleteButton({ onDelete, what = 'post' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  async function click() {
    if (busy) return;
    const ok = await confirm({
      title: `Delete your ${what}?`,
      body: what === 'reply'
        ? "It comes off the thread for good — this can't be undone."
        : `This can't be undone, and any replies to your ${what} go with it.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Keep it',
    });
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await onDelete();
    } catch (err) {
      setError(friendlyCallError(err));
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="post-action post-action--quiet" onClick={click} disabled={busy}>
        {busy ? 'Deleting…' : 'Delete'}
      </button>
      {error && <span className="post-report__error" role="alert">{error}</span>}
      {confirmDialog}
    </>
  );
}

// "Report" opens a short list of reasons in place. Reporting is anonymous to
// the author; three reports from different networks hold the post for review
// (functions/reviews.js reportContent).
// `send(reason)` overrides what gets reported — the video shelf passes one
// that calls reportVideo instead.
export function ReportButton({ businessId, postId, replyId, ensureGuest, send: sendOverride }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle'); // idle | sending | done
  const [error, setError] = useState('');

  async function send(reason) {
    setState('sending');
    setError('');
    try {
      await ensureGuest();
      if (sendOverride) await sendOverride(reason);
      else await reportContent({ businessId, postId, replyId: replyId || null, reason });
      setState('done');
      setOpen(false);
    } catch (err) {
      setError(friendlyCallError(err));
      setState('idle');
    }
  }

  if (state === 'done') return <span className="post-reported">Reported. Thanks.</span>;

  return (
    <span className="post-report">
      <button type="button" className="post-action post-action--quiet" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        Report
      </button>
      {open && (
        <span className="post-report__menu" role="menu">
          <span className="post-report__title">What's wrong with it?</span>
          {REPORT_REASONS.map((r) => (
            <button key={r.id} type="button" role="menuitem" disabled={state === 'sending'} onClick={() => send(r.id)}>
              {r.label}
            </button>
          ))}
          {error && <span className="post-report__error" role="alert">{error}</span>}
        </span>
      )}
    </span>
  );
}
